import React, { useEffect } from 'react';
import { useStore } from '../../../../../stores/root';
import styles from './styles.module.scss';

// assets
import { faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import ErrorService from '../../../../../services/general/error';
import BankAccountService from '../../../../../services/general/bankAccount';

// props
import { IBankAccountResourceShortProps } from '../../../../../props/general/bankAccount';

// components
import { ActionButton, CommandBar, IColumn, ICommandBarItemProps, PrimaryButton, SearchBox, SelectionMode, ShimmeredDetailsList, Stack } from '@fluentui/react';
import Text from '../../../../typography/text';
import BankAccountForm from '../form';

interface IBankAccountsListProps {
}

const BankAccountsList: React.FC<IBankAccountsListProps> = (props: IBankAccountsListProps) => {
    const { banner } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [bankAccounts, setBankAccounts] = React.useState<IBankAccountResourceShortProps[]>([]);
    const [activeBankAccount, setActiveBankAccount] = React.useState<IBankAccountResourceShortProps | undefined>();
    const [keyword, setKeyword] = React.useState<string>("");
    const [activeSurface, setActiveSurface] = React.useState<string | undefined>();

    const columns: IColumn[] = [
        {
            key: "name",
            fieldName: "name",
            name: "Name",
            minWidth: 300,
            maxWidth: 300,
            onRender: (item: IBankAccountResourceShortProps) => {
                return <Stack styles={{root: {height: '100%'}}}>
                    <Text>{item.name}</Text>
                </Stack>;
            }
        },
        {
            key: "actions",
            name: "",
            minWidth: 50,
            maxWidth: 50,
            onRender: (item: IBankAccountResourceShortProps) => {
                return <Stack styles={{root: {height: '100%'}}}>
                    <Stack.Item className={"detailsListActionRow"}>
                        <ActionButton className={'detailsListActionButton'} menuProps={{
                            items: [
                                {key: "update", text: "Update", iconProps: {iconName: "Edit"}, onClick: () => {
                                    setActiveBankAccount(item);
                                    setActiveSurface('update');
                                }}
                            ]
                        }} />
                    </Stack.Item>
                </Stack>;
            }
        },
        {
            key: "accountNumber",
            fieldName: "accountNumber",
            name: "Account Number",
            minWidth: 200,
            maxWidth: 200,
            isMultiline: true,
            onRender: (item: IBankAccountResourceShortProps) => {
                return <Stack styles={{root: {height: '100%'}}}>
                    <Text>{item.accountNumber}</Text>
                </Stack>;
            }
        },
        {
            key: "bankName",
            fieldName: "bankName",
            name: "Bank Name",
            minWidth: 200,
            maxWidth: 200,
            isMultiline: true,
            onRender: (item: IBankAccountResourceShortProps) => {
                return <Stack styles={{root: {height: '100%'}}}>
                    <Text>{item.bankName}</Text>
                </Stack>;
            }
        },
        {
            key: "description",
            fieldName: "description",
            name: "Description",
            minWidth: 0,
            isMultiline: true,
            onRender: (item: IBankAccountResourceShortProps) => {
                return <Stack styles={{root: {height: '100%'}}}>
                    <Text>{item.description}</Text>
                </Stack>;
            }
        }
    ];

    useEffect(() => {
        _onRetrieveBankAccounts();
    }, []);

    const _onRetrieveBankAccounts = async (pageNumber?: number, ) => {
        try {
            if (!pageNumber) {
                setLoaded(false);
            }

            const qs: string[] = [];
            const results = await BankAccountService.retrieve();
            setBankAccounts(results);
            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'retrieve_bank_accounts_error',
                text: 'Failed to retrieve bank accounts. Error: ' + ErrorService.getMessage(e),
                icon: faXmarkCircle,
                variant: 'error'
            });
        }
    }

    const _onKeywordChanged = (value?:string) => {
        setLoaded(true);
        setKeyword(value || "");
    }

    const getCommandBarItems = () => {
        let items: ICommandBarItemProps[] = [];
        let farItems: ICommandBarItemProps[] = [];

        items.push({
            key: "search",
            onRender: () => {
                return <SearchBox placeholder={"Search ..."} onSearch={_onKeywordChanged} />
            }
        });

        farItems.push({
            key: "register",
            text: "Create Bank Account",
            iconProps: { iconName: "Add" },
            onRender: () => { 
                return <PrimaryButton text={"Create Bank Account"} 
                    iconProps={{ iconName: "Add" }} 
                    onClick={() => {setActiveSurface('create') }} 
                    styles={{ root: { marginLeft: 10 } }} />;
            }
        });

        return { items, farItems };
    }

    const _onSurfaceDismissed = (refresh?: boolean) => {
        setActiveSurface(undefined);
        setActiveBankAccount(undefined);

        if (refresh) {_onRetrieveBankAccounts()}
    }

    return <Stack className={styles.container} tokens={{childrenGap: 20}}>
        <CommandBar
            items={getCommandBarItems().items}
            farItems={getCommandBarItems().farItems}
            styles={{root: {
                padding: 0,
                height: 'unset',
                backgroundColor: 'transparent'
            }}}
            ariaLabel="Use left and right arrow keys to navigate between commands" />
        <Stack className={styles.list}>
            {
                !loaded || (loaded && bankAccounts.length > 0) ? (
                    <>
                        <ShimmeredDetailsList
                            setKey="items"
                            items={bankAccounts}
                            columns={columns}
                            selectionMode={SelectionMode.none}
                            enableShimmer={!loaded}
                            onShouldVirtualize={() => false}
                            ariaLabelForShimmer="Content is being fetched"
                            ariaLabelForGrid="Item details" />
                    </>
                ) : null
            }
            {loaded && bankAccounts.length < 1 ? <Stack styles={{root: {padding: 10}}}>
                <Text>Bank account(s) not found</Text>
            </Stack> : null}
        </Stack>
        {activeSurface === 'create' ? <BankAccountForm onDismissed={_onSurfaceDismissed} /> : null}
        {activeSurface === 'update' && activeBankAccount ? <BankAccountForm bankAccount={activeBankAccount} onDismissed={_onSurfaceDismissed} /> : null}
    </Stack>;
};

export default BankAccountsList;
